import { Injectable } from '@angular/core';
import {forkJoin, Observable, of, from, defer} from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map, switchMap, tap, mergeMap, toArray, finalize, shareReplay } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {DataService, ObjectAttachRequest, ObjectDetachRequest} from "./data-service";
import {DataObject} from "../../model/DataObject";
import {DataNotificationClient} from "./DataNotificationClient";
import {Subject} from "rxjs";
import {NotificationsService} from "./notifications.service";
import {chunkIds, collectionRequestSignature, DEFAULT_ID_CHUNK_SIZE, idChunkSize as computeIdChunkSize, idPropertyFromQualifiedKey, rowId, unrequestedIds} from "./collection-paging";
import {retryTransient} from "./http-error";

@Injectable()
export abstract class AbstractLiveDataService<T extends DataObject> extends DataService implements DataNotificationClient<T> {
  public objects: T[] = [];
  public objectsById = {};
  public objectAttachRequests: any = {};
  public objectDetachRequests: any = {};

  private objectsObservable = of( this.objects );
  public loadingState: string = 'INITIAL';
  public addNotifier: Subject<T> = new Subject<T>();
  public updateNotifier: Subject<T> = new Subject<T>();
  public deleteNotifier: Subject<T> = new Subject<T>();

  private currentFilter: any;
  private currentReset: boolean;
  private currentLimit: number;
  private currentParallelRequestsLimit: number;

  /**
   * The collection load that is running right now, and the request it
   * was issued for.
   *
   * ## Why this is needed
   *
   * `loadingState === 'LOADING'` was supposed to be the guard against
   * issuing the same collection load twice, and it does not work,
   * because the caller most likely to ask twice clears it first:
   * `StartupComponent.getData()` opens with
   *
   * ```typescript
   * for (const serviceName of Object.keys(this.notificationsService.services)) {
   *   this.notificationsService.services[serviceName].loadingState = 'INITIAL';
   * }
   * ```
   *
   * and `getData()` runs more than once per sign-in - it is called from
   * `roleChanged()` AND from `isLoggedIn()`, and both fire while
   * signing in (`setJwtToken()` selects a role, which notifies the
   * role-change listeners, then `checkSession()` completes and invokes
   * the `LoggedInCallback`). Every `StartupComponent` ever constructed
   * was on the role-change listener list as well, because until
   * 2026-09-09 there was no way to deregister one.
   *
   * Each of those calls re-entered `getFilteredItems()` with
   * `loadingState` freshly set to `'INITIAL'`, so the guard passed and
   * the ENTIRE request set went out again - for PrintNodeOrder at
   * startup that is one id request plus one request per chunk of ids,
   * duplicated, against an API that had already been asked.
   *
   * Keying on the request itself instead of on a mutable status field
   * fixes that: a second identical load joins the first rather than
   * racing it. The signature carries the role, so a role switch is
   * never mistaken for a duplicate.
   */
  private inFlightRequestSignature: string = '';
  private inFlightRequest: Observable<T[]> = undefined;

  abstract makeObjectInstance(args: any): T;
  abstract getLoggingObjectTypeName(): string;
  abstract supportsParallel(): boolean;
  abstract getURLEndPoint(): string;
  // abstract linkObject(newObject: T, rawObject: T);
  // abstract updateLinks(existingObject: T, newObject: T, rawObject: T);
  abstract getLinkData(): any[];

  protected constructor(protected http: HttpClient, protected messageService: MessageService, protected startupService: StartupService, protected notificationsService: NotificationsService) {
    super(startupService);
  }

  protected objectPropertiesToJSONifyWhenSending(): string[] {
    return [];
  }
  public hash(s: string) {
    return s.split("").reduce(function(a, b) {
      a = (( a << 5 ) - a) + b.charCodeAt(0);
      return a & a;
    } , 0 );
  }

  reset () {
    this.loadingState = 'INITIAL';
    this.objectAttachRequests = {};
    this.objectDetachRequests = {};
    this.objectsById = {};
    // A load that is still running is no longer the load anybody wants
    // once the cache has been thrown away (this runs on logout and on
    // every role switch). Dropping the handle does not cancel those
    // requests - the in-flight `selectedRole` checks already discard
    // their results - it just stops a later caller from joining a load
    // that is about to be ignored.
    this.inFlightRequest = undefined;
    this.inFlightRequestSignature = '';
    if ( this.objects) {
      while ( this.objects.length > 0) {
        this.objects.pop();
      }
    }
  }

  /**
   * Identity of a collection request: two calls with the same signature
   * would put the identical bytes on the wire, so the second can join
   * the first instead of repeating it.
   *
   * The role is part of it deliberately - the same filter under a
   * different `selectedRole` is a genuinely different request (the role
   * travels in the `selectedrole` header and scopes the result).
   */
  private requestSignature(shape: string, filter: any, limit: number): string {
    return collectionRequestSignature(
      shape,
      this.getURLEndPoint(),
      this.startupService.selectedRole,
      this.startupService.selectedRoleId,
      limit,
      filter
    );
  }

  /**
   * Publish `request` as THE in-flight load for `signature` and hand
   * back a shareable handle to it.
   *
   * `shareReplay` with `refCount: false` means the underlying requests
   * run exactly once no matter how many callers subscribe, and that a
   * caller arriving late still gets the result rather than triggering a
   * second round trip.
   */
  private shareInFlightRequest(signature: string, request: Observable<T[]>): Observable<T[]> {
    const shared = request.pipe(
      finalize(() => {
        if ( this.inFlightRequestSignature === signature ) {
          this.inFlightRequest = undefined;
          this.inFlightRequestSignature = '';
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.inFlightRequest = shared;
    this.inFlightRequestSignature = signature;
    return shared;
  }

  /**
   * The already-running load for `signature`, or `undefined`.
   *
   * Logs when it hands one back, because "the request I expected did
   * not appear in the network tab" needs an explanation on the console
   * or it reads as a dropped load.
   */
  private joinInFlightRequest(signature: string): Observable<T[]> {
    if ( this.inFlightRequest && this.inFlightRequestSignature === signature ) {
      console.log(`${this.getLoggingObjectTypeName()}: an identical load is already running - joining it instead of issuing ${this.getURLEndPoint()} again`);
      // A joiner MUST re-assert LOADING, and this line is load-bearing for
      // the startup screen.
      //
      // `StartupComponent.getData()` sets EVERY service to 'INITIAL' before
      // it starts, and `StartupComponent.checkIsLoaded()` blocks only on
      // 'LOADING' / 'ERROR' - 'INITIAL' means "this service is not part of
      // this role's load", which is how services nobody asked for are
      // excluded. A service that joined an existing load and returned early
      // therefore sat at 'INITIAL' with a request genuinely outstanding, and
      // the first OTHER service to finish found nothing blocking: startup
      // navigated to the role's default screen immediately and the data
      // arrived a second later, painting an empty screen first. Reported
      // 2026-09-10; introduced with the de-duplication on 2026-09-09.
      this.loadingState = 'LOADING';
      return this.inFlightRequest.pipe(
        tap({
          // Belt and braces for joining a load that has already completed
          // but whose `finalize` has not yet cleared the slot. `shareReplay`
          // replays the cached VALUE; it does not re-run the pipeline that
          // sets 'LOADED', so without this the service would sit at
          // 'LOADING' forever and `checkIsLoaded()` would never navigate at
          // all - a worse failure than the one above. Only promotes a state
          // this call set itself, so an 'ERROR' from `handleErrorArray`
          // still stands.
          complete: () => {
            if ( this.loadingState === 'LOADING' ) {
              this.loadingState = 'LOADED';
            }
          }
        })
      );
    }
    return undefined;
  }

  reload(): Observable<T[]> {
    if ( this.currentFilter instanceof Object ) {
      return this.getFilteredItems(this.currentFilter, this.currentReset, this.currentLimit, this.currentParallelRequestsLimit);
    } else {
      return this.getItems(this.currentReset, this.currentLimit);
    }
  }

  public implementsReload() {
    return true;
  }


  getAddNotifier(flood: boolean) {
    if ( flood ) {
      this.objects.forEach( o => {
        this.addNotifier.next(o);
      });
    }
    return this.addNotifier;
  }

  getUpdateNotifier() {
    return this.updateNotifier;
  }

  getDeleteNotifier() {
    return this.deleteNotifier;
  }

  getItems(reset: boolean = true, limit: number = 25): Observable<T[]> {
    // set the current call values incase we want to call reload
    this.currentFilter = undefined;
    this.currentReset = reset;
    this.currentLimit = limit;

    const signature = this.requestSignature('items', undefined, limit);
    const alreadyRunning = this.joinInFlightRequest(signature);
    if ( alreadyRunning ) {
      return alreadyRunning;
    }

    if ( !this.loadingState || this.loadingState === 'INITIAL' || ( reset && this.loadingState !== 'LOADING' )) {
      if ( this.loadingState === 'LOADING' ) {
        console.log(`Already Loading A${this.getURLEndPoint()}`);
      }
      this.reset();
      this.loadingState = 'LOADING';
      const url = `${environment.apiBaseURL}${this.getURLEndPoint()}`;
      return this.shareInFlightRequest(signature, this.getRecursiveItems(url, 1, limit, '', this.startupService.selectedRole));
    } else {
      return this.objectsObservable;
    }
  }

  protected supportsLimitedParallel(): boolean {
    return false;
  }

  /**
   * How many ids go into ONE limited-parallel chunk request.
   *
   * Deliberately not `limit`. `limit` is the caller's page size, and at
   * startup it is small on purpose - `startup.component.ts` asks for
   * PrintNodeOrders with `limit` 10 so that the count+parallel-pages
   * strategy would use small pages. Using it as the chunk size too made
   * the limited-parallel strategy issue `ceil(total / 10)` requests, all
   * of them `page=1&limit=10` and distinguishable only by the base64
   * filter: a facility with 900 open orders opened its session with 90
   * near-identical round trips. That is what "it keeps asking for the
   * same 10 items over and over" looks like from the outside.
   *
   * The id-chunk strategy has no reason to care about the page size at
   * all - the ids are already known, and the server is required to
   * honour an explicit `limit` (CollectionPaging.md requirement 4), so
   * one request can carry the whole chunk. 100 keeps the response and
   * the URL (the ids travel in the base64 filter) to a sane size while
   * cutting the request count by an order of magnitude.
   *
   * Overridable for an entity whose rows are unusually heavy.
   */
  protected idChunkSize(limit: number): number {
    return computeIdChunkSize(limit, DEFAULT_ID_CHUNK_SIZE);
  }

  protected objectQualifiedPrimaryKeyId(): string {
    return 'mustBeChangedToClassAliasInQuery.ID';
  }

  /**
   * The property on a RAW server row that carries this entity's primary
   * key - `ID` for most entities, `package_ID` for Package.
   *
   * Derived from `objectQualifiedPrimaryKeyId()` so the two can never
   * disagree. Do not read `row.ID` directly anywhere in this class;
   * roughly half the time it is `undefined`, and every consumer of it
   * treats `undefined` as a meaningful value rather than as an error.
   */
  protected objectIdPropertyName(): string {
    return idPropertyFromQualifiedKey(this.objectQualifiedPrimaryKeyId());
  }

  /**
   * Fetches filtered items from the server using different strategies:
   * 1. Limited Parallel (new): Fetches all IDs, then chunks them and makes parallel requests with a concurrency limit.
   * 2. Full Parallel (existing): Fetches count, then makes all page requests in parallel.
   * 3. Sequential (existing): Fetches items recursively page by page.
   *
   * @param filter The filter object to apply.
   * @param reset Whether to reset the local items before fetching.
   * @param limit The number of items to fetch per request.
   * @param parallelRequestsLimit The maximum number of concurrent requests for limited parallel fetching (defaults to 3).
   * @returns An Observable of the fetched items.
   */
  getFilteredItems(filter: any, reset: boolean = true, limit: number = 25, parallelRequestsLimit: number = 3): Observable<T[]> {
    // set the current call values incase we want to call reload
    this.currentFilter = filter;
    this.currentReset = reset;
    this.currentLimit = limit;
    this.currentParallelRequestsLimit = parallelRequestsLimit;

    const signature = this.requestSignature('filtered', filter, limit);
    const alreadyRunning = this.joinInFlightRequest(signature);
    if ( alreadyRunning ) {
      return alreadyRunning;
    }

    if (this.loadingState === 'INITIAL' || (reset && this.loadingState !== 'LOADING')) {
      if (this.loadingState === 'LOADING') {
        console.log(`Already Loading B${this.getURLEndPoint()}`);
      }
      this.reset();
      this.loadingState = 'LOADING';

      const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/filter`;
      const selectedRole = this.startupService.selectedRole;

      // Deep copy filter to avoid modifying the original object passed in
      const baseFilter = JSON.parse(JSON.stringify(filter));
      const baseFilterString = `filter=${Buffer.from(JSON.stringify(baseFilter), 'binary').toString('base64')}`;


      // --- NEW: Limited Parallel Fetching Logic ---
      if (this.supportsParallel() && this.supportsLimitedParallel()) {
        const chunkSize = this.idChunkSize(limit);
        console.log(`${this.getLoggingObjectTypeName()} getItems with Filter limit[${limit}] idChunkSize[${chunkSize}] running in Limited Parallel (Concurrency: ${parallelRequestsLimit})`);

        // Step 1: Fetch all IDs first
        let allIdsUrl = `${url}?page=-2`; // Assuming 'idsOnly=true' gets a list of IDs
        if (baseFilterString) {
          allIdsUrl += '&' + baseFilterString;
        }
        allIdsUrl += '&liveObjects=true';
        this.messageService.add(`${this.getLoggingObjectTypeName()}Service: fetching all IDs`);

        return this.shareInFlightRequest(signature, this.http.get<number[]>(allIdsUrl, this.startupService.getHttpOptions())
          .pipe(
            switchMap(idResponse => {
              if (selectedRole !== this.startupService.selectedRole) {
                console.log(`SWITCHED ROLES during ID fetch. Aborting.`);
                this.loadingState = 'LOADED';
                return this.objectsObservable;
              }
              const allObjectIds: number[] = idResponse as number[]; // TODO: Confirm the return type
              if (!allObjectIds || !allObjectIds.length) {
                // `page=-2` must answer with an id array. A scalar (a
                // count) or an object arrives here with no `length`, and
                // silently loading nothing is the one outcome that is
                // impossible to tell apart from "there is no data".
                if (allObjectIds && !Array.isArray(allObjectIds)) {
                  console.error(`${this.getLoggingObjectTypeName()}: GET ${url}?page=-2 did not return an array of ids `
                    + `(got ${typeof allObjectIds}). Limited-parallel loading cannot run and NOTHING was loaded. `
                    + `See docs/contracts/shared/CollectionPaging.md requirement 2.`);
                }
                this.loadingState = 'LOADED';
                return this.objectsObservable;
              }
              console.log(`Fetched ${allObjectIds.length} object IDs.`);

              // Step 2: Break IDs into chunks
              const idChunks: number[][] = chunkIds(allObjectIds, chunkSize);
              console.log(`Broken into ${idChunks.length} ID chunks.`);

              // Tripped by the first chunk response that comes back
              // carrying rows nobody asked for, which means the server
              // did not apply the `in` clause. Every remaining chunk
              // would then return that same first page again, so the
              // rest of the requests are abandoned rather than fired.
              // Without this the failure mode is a burst of identical
              // requests, one per chunk, each re-delivering the same
              // rows - expensive, and it looks like a client loop.
              let idFilterIgnored = false;

              // Step 3 & 4: Create requests for each chunk and execute with limited concurrency
              const requests = idChunks.map(chunk => {
                const chunkFilter = JSON.parse(JSON.stringify(baseFilter)); // Deep copy original filter
                if (!chunkFilter.in) {
                  chunkFilter.in = {};
                }
                chunkFilter.in[this.objectQualifiedPrimaryKeyId()] = chunk; // Add the 'ID' property with the current chunk

                const chunkFilterString = `filter=${Buffer.from(JSON.stringify(chunkFilter), 'binary').toString('base64')}`;
                // NOTE: We MUST send explicit `page` + `limit` here.
                // The server (see PrintNodeOrderRoutes.ts /filter) defaults
                // `limit` to 10 and `page` to 1 when either is missing -
                // so a chunk of e.g. 25 IDs would silently return only
                // the first 10 rows, dropping the remainder on the
                // floor. We know exactly how many rows this chunk asks
                // for (it's the size of `chunk`), so pin page=1 and
                // limit=chunk.length to make the server return all of
                // them in a single response.
                let chunkUrl = `${url}?page=1&limit=${chunk.length}&liveObjects=true`;
                if (chunkFilterString) {
                  chunkUrl += '&' + chunkFilterString;
                }
                // `defer` so the abandon check happens when this request
                // is about to run, not when the array is built - by then
                // an earlier chunk may already have proved that the
                // server is ignoring the id filter.
                return defer(() => {
                  if (idFilterIgnored) {
                    return of(false);
                  }
                  this.messageService.add(`${this.getLoggingObjectTypeName()}Service: fetching items for chunk`);
                  return this.http.get<T[]>(chunkUrl, this.startupService.getHttpOptions())
                    .pipe(
                      map(items => {
                        if (selectedRole !== this.startupService.selectedRole) {
                          console.log(`SWITCHED ROLES during chunk fetch. Ignoring result.`);
                          return false; // Indicate that this result should be ignored
                        }
                        // Merge first: rows that were not requested are
                        // still valid rows for the base filter, and
                        // dropping them would lose data on top of
                        // whatever else is wrong.
                        for (const item of items) {
                          this.addUpdateLocalItem(item);
                        }
                        const unrequested = unrequestedIds(items, chunk, this.objectIdPropertyName());
                        if (unrequested.length > 0 && !idFilterIgnored) {
                          idFilterIgnored = true;
                          console.error(`${this.getLoggingObjectTypeName()}: GET ${url} ignored the `
                            + `in[${this.objectQualifiedPrimaryKeyId()}] clause - it answered a request for `
                            + `${chunk.length} specific id(s) with ${unrequested.length} row(s) that were not asked for `
                            + `(e.g. id ${unrequested[0]}). Every remaining chunk would return that same page, `
                            + `so the other ${idChunks.length - 1} chunk request(s) are being abandoned and this `
                            + `collection is now INCOMPLETE (${this.objects.length} object(s) loaded of `
                            + `${allObjectIds.length}). Either the /filter route needs to support the 'in' operator or `
                            + `${this.getLoggingObjectTypeName()}Service must stop overriding supportsLimitedParallel(). `
                            + `See docs/contracts/shared/CollectionPaging.md.`);
                          return false;
                        }
                        return true; // Indicate success
                      }),
                      catchError(val => {
                        console.error(`Error fetching chunk: ${val}`);
                        return of(false); // Return false on error to indicate failure for this chunk
                      })
                    );
                });
              });

              // Use mergeMap with concurrency limit
              return from(requests).pipe(
                mergeMap(requestObs => requestObs, parallelRequestsLimit), // Limiting concurrency
                toArray(), // Collect all results after all requests complete
                map(results => {
                  results.forEach(success => {
                    if (!success) {
                      // One or more requests failed or were ignored due to role change
                      console.warn('One or more limited parallel requests failed or were ignored.');
                      // Decide how to handle this: return current objects, or throw an error
                      // For now, we'll continue and return what we have.
                    }
                  });
                  this.loadingState = 'LOADED';
                  return this.objects;
                })
              );
            }),
            catchError(this.handleErrorArray(`getFilteredItems (Limited Parallel) ${baseFilterString}`))
          ));
      } else {
        if (this.supportsParallel()) {
          console.log(`getCount ${url}`);
          let countUrl = `${url}?page=-1`;
          if (baseFilterString) { // Use baseFilterString
            countUrl += '&' + baseFilterString;
          }
          countUrl += '&liveObjects=true';
          this.messageService.add(`${this.getLoggingObjectTypeName()}Service: fetching count`);
          return this.shareInFlightRequest(signature, this.http.get<T[]>(countUrl, this.startupService.getHttpOptions())
            .pipe(
              switchMap(countResponse => {
                if (selectedRole !== this.startupService.selectedRole) {
                  return this.objectsObservable;
                } else {
                  if (Number(countResponse) === 0) {
                    this.loadingState = 'LOADED';
                    return this.objectsObservable;
                  }
                }
                if (countResponse) {
                  console.log(`Count Objects ${countResponse}`);
                }
                const numberOfPagesToRunInParallel = Math.ceil(Number(countResponse) / limit);
                const requests = [];
                for (let page = 1; page <= numberOfPagesToRunInParallel; page++) {
                  let pageUrl = `${url}?page=${page}&limit=${limit}`;
                  if (baseFilterString) { // Use baseFilterString
                    pageUrl += '&' + baseFilterString;
                  }
                  pageUrl += '&liveObjects=true';
                  this.messageService.add(`${this.getLoggingObjectTypeName()}Service: fetching items`);
                  requests.push(
                    this.http.get<T[]>(pageUrl, this.startupService.getHttpOptions())
                      .pipe(
                        map(items => {
                          if (selectedRole !== this.startupService.selectedRole) {
                            console.log(`SWITCHED ROLES -- selectedRoleWhenRequestWasMade[${selectedRole}] startupServiceSelectedRole[${this.startupService.selectedRole}] ${this.getLoggingObjectTypeName()} getItems with Filter limit[${limit}] totalItems[${items.length}] running in Parallel`);
                            return false;
                          }
                          if (this.getLoggingObjectTypeName() === 'EcommerceOrder') {
                            console.log(`${this.getLoggingObjectTypeName()} getItems ${page} ${limit} before addUpdateLocalItem ${items.length}`);
                          }
                          for (const item of items) {
                            this.addUpdateLocalItem(item);
                          }
                          return true;
                        }),
                        catchError(val => of(`I caught: ${val}`))
                      )
                  );
                }
                return forkJoin(requests).
                pipe(
                  map(r => {
                    r.forEach(a => {
                      if (!a) {
                        // The selected role changed or request failed -- ignore this result
                        return this.objects;
                      }
                    });
                    this.loadingState = 'LOADED';
                    return this.objects;
                  })
                );
              }),
              catchError(this.handleErrorArray(`getFilteredItems ${baseFilterString}`))
            ));
        } else {
          console.log(`${this.getLoggingObjectTypeName()} getItems with Filter limit[${limit}] not Parallel`);
          return this.shareInFlightRequest(signature, this.getRecursiveItems(url, 1, limit, baseFilterString, selectedRole)); // Use baseFilterString
        }
      }
    } else {
      return this.objectsObservable;
    }
  }

  /**
   * Sequential paging fallback, used by `getItems()` (against the plain
   * `GET /{endpoint}`) and by `getFilteredItems()` for services that do
   * not opt into `supportsParallel()`.
   *
   * ## Termination, and why it needs a guard
   *
   * The natural stop condition is "a page came back shorter than the
   * limit". That is only sound if the server actually pages. As of
   * 2026-09-02 **no** root `GET /{endpoint}` route on the server reads
   * `req.query.page` / `req.query.limit` - every one of them returns the
   * whole collection on every request (verified across all 53
   * `*Routes.ts` modules). So today this method issues one request,
   * receives all N rows, sees `N !== limit`, and stops. It works by
   * coincidence.
   *
   * The coincidence fails when **N === limit exactly**. Then the client
   * asks for page 2, the server ignores the page and returns the same N
   * rows again, `items.length === limit` still holds, and the recursion
   * never ends: an infinite request loop against the API, on the startup
   * path. With `draftService.getItems(true, 25)` that is an organization
   * owning exactly 25 Drafts. Nothing about that is exotic.
   *
   * The guard below detects the "server ignored my page parameter" case
   * directly: if a page returns exactly the same set of ids as the page
   * before it, paging is not happening and continuing cannot make
   * progress. A correctly-paged endpoint never repeats a page, so this
   * costs those endpoints nothing.
   *
   * `MAX_PAGES` is a second backstop for a server that pages
   * incorrectly in some other way (e.g. always returning page 1 with
   * different ordering). Both paths log loudly, because a silent cap
   * would look like "some data just doesn't load".
   */
  private static readonly MAX_PAGES = 500;

  private getRecursiveItems(baseUrl: string, page: number, limit: number, filterString: string, selectedRole: string, previousPageIds: string = ''): Observable<T[]> {
    console.log(`getRecursiveItems ${baseUrl} ${page} ${limit}`);
    let url = `${baseUrl}?page=${page}&limit=${limit}`;
    if ( filterString ) {
      url += '&' + filterString;
    }
    url += '&liveObjects=true';
    this.messageService.add(`${this.getLoggingObjectTypeName()}Service: fetched items`);
    return this.http.get<T[]>(url, this.startupService.getHttpOptions())
      .pipe(
        switchMap(items => {
          if ( selectedRole !== this.startupService.selectedRole ) {
            console.log(`SWITCHED ROLES -- selectedRoleWhenRequestWasMade[${selectedRole}] startupServiceSelectedRole[${this.startupService.selectedRole}] ${this.getLoggingObjectTypeName()} getItems with Filter limit[${limit}] totalItems[${items.length}]`);
            return this.objectsObservable;
          }
          if (this.getLoggingObjectTypeName() === 'EcommerceOrder') {
            console.log(`${this.getLoggingObjectTypeName()} getItems ${page} ${limit} before addUpdateLocalItem ${items.length}`);
          }

          // Signature of THIS page, computed before the items are merged
          // into the local cache (merging does not change ids, but doing
          // it first keeps the comparison obviously about the response).
          const idProperty = this.objectIdPropertyName();
          const pageIds = (items || []).map(i => rowId(i, idProperty)).sort().join(',');

          for ( const item of items ) {
            this.addUpdateLocalItem(item);
          }

          if ( items.length !== limit ) {
            console.log(`${this.getLoggingObjectTypeName()} getItems ${page} ${limit} DONE ${items.length}`);
            this.loadingState = 'LOADED';
            return this.objectsObservable;
          }

          if ( pageIds && pageIds === previousPageIds ) {
            console.error(`${this.getLoggingObjectTypeName()}: GET ${baseUrl} returned an identical page for page=${page} and page=${page - 1}. `
              + `The endpoint is ignoring page/limit, so paging cannot make progress - stopping here with ${this.objects.length} object(s) loaded. `
              + `This is the "row count equals the page limit" case; the endpoint needs real paging (or a /filter route) to be correct.`);
            this.loadingState = 'LOADED';
            return this.objectsObservable;
          }

          if ( page >= AbstractLiveDataService.MAX_PAGES ) {
            console.error(`${this.getLoggingObjectTypeName()}: GET ${baseUrl} hit the ${AbstractLiveDataService.MAX_PAGES}-page ceiling with `
              + `${this.objects.length} object(s) loaded. Refusing to continue. Either the collection is far larger than this path is `
              + `designed for, or the endpoint is not paging correctly.`);
            this.loadingState = 'LOADED';
            return this.objectsObservable;
          }

          return this.getRecursiveItems(baseUrl, page + 1, limit, filterString, selectedRole, pageIds);
        }),
        catchError(this.handleErrorArray(`getRecursiveItems ${page} ${limit} ${filterString}`))
      );
  }

  getItem(id: number): Observable<T> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${id}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} : fetched item id=${id}`);
    return this.http.get<T>(url, this.startupService.getHttpOptions())
      .pipe(
        map(o => {
          return this.addUpdateLocalItem(o);
        }),
        catchError(this.handleError(`get ${this.getLoggingObjectTypeName()} id=${id}`))
    );
  }

  updateItem (item: T): Observable<T> {
    let object = {};
    if ( item && typeof (item as any)?.toJSON === 'function') {
      object = (item as any).toJSON();
    } else {
      for (const prop of Object.keys(item)) {
        if ( !(item[prop] instanceof Array ) && ( typeof item[prop] === 'string' || typeof item[prop] === 'number' || typeof item[prop] === 'boolean' || item[prop] instanceof String || item[prop] instanceof Number || item[prop] instanceof Boolean || item[prop] instanceof Date )) {
          object[prop] = item[prop];
        } else {
          if (!(item[prop] instanceof Array )) {
            // Log this with a break point to make sure we have the right conditions in the above if statement
            console.log(`Rejecting saving property ${prop} of ${this.getLoggingObjectTypeName()}`);
          }
        }
      }
    }

    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${item.getId()}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()}: updated id=${item.getId()}`);

    return this.http.put<T>(url, object, this.startupService.getHttpOptions()).pipe(
      tap(o => {
        console.log(`A ObjectType[${this.getLoggingObjectTypeName()}]`);
        this.log(`updated ${this.getLoggingObjectTypeName()} id=${item.getId()}`);
        console.log(`B ObjectType[${this.getLoggingObjectTypeName()}]`);
        return this.addUpdateLocalItem(o);
      }),
      catchError(this.handleError(`update ${this.getLoggingObjectTypeName()}`))
    );
  }


  /**
   * POST a new row.
   *
   * ## Retry policy
   *
   * This used to be `retry(6)` -- seven attempts at whatever went wrong, with
   * no look at *what* went wrong. That is the defect `http-error.ts` was
   * written to kill (see `tests/HttpErrorPolicy.smoke.ts`), and it survived
   * here because the fix was applied to the order path rather than to the base
   * class every POST in the app goes through.
   *
   * Two ways it bites, both observed:
   *
   * - **A 4xx is a decision, not a hiccup.** `POST /ecommerceproduct` now
   *   answers 400 / 403 / 404 / 409; re-sending a 409 six more times cannot
   *   change the answer and only delays the error reaching the operator.
   * - **A retried POST creates duplicates.** The request may well have
   *   succeeded at the server and lost the response, so each retry is another
   *   insert attempt. This is how one operator click became the duplicate-key
   *   incident of 2026-09-16.
   *
   * `retryTransient(1)` retries only genuine transport failures (no response,
   * 408, 425, 5xx) and only once, which recovers a dropped connection or a 503
   * mid-deploy while bounding the duplicate window to a single extra attempt.
   */
  addItem (item: T): Observable<T> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}`;
    return this.http.post<T>(url, item, this.startupService.getHttpOptions())
      .pipe( retryTransient<T>(1),
        map(o => {
          return this.addUpdateLocalItem(o);
        }),
      catchError(this.handleError(`add ${this.getLoggingObjectTypeName()}`))
    );
  }

  deleteItem (item: T): Observable<T> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${item.getId()}`;
    // TODO: Remove the object from the objects list
    const index = this.objects.indexOf(item);
    if ( index >= 0 ) {
      this.objects.splice(index, 1);
    }
    return this.http.delete<T>(url, this.startupService.getHttpOptions()).pipe(
      tap(o => {
        this.deleteLocalItem(o);
        this.log(`deleted ${this.getLoggingObjectTypeName()} id=${item.getId()}`);
      }),
      catchError(this.handleError(`delete ${this.getLoggingObjectTypeName()}`))
    );
  }

  // handleError<T> (role: string, operation = 'operation', result?: T) {
  protected handleError (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  // handleError<T> (role: string, operation = 'operation', result?: T) {
  protected handleErrorArray (operation = 'operation', result?: T[]) {
    return (error: any): Observable<T[]> => {
      this.loadingState = 'ERROR';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T[]);
    };
  }
  protected log(message: string) {
    this.messageService.add(`${this.getLoggingObjectTypeName()} Service:` + message);
  }

  public getLocalItem(id: number): T {
    return this.objectsById['' + id];
    // for (const item of this.objects) {
    //   if (item.getId() === id) {
    //     return item;
    //   }
    // }
    // return null;
  }


  addUpdateLocalItem (rawObject: T): T {
    if ( rawObject['facilityInventoryProducts'] && rawObject['facilityInventoryProducts'].length > 0 ) {
      console.log(`Product with facilityInventoryProducts`);
    }
    const o = this.makeObjectInstance(rawObject);
    const existingObj = this.getLocalItem(o.getId());
    if ( !existingObj ) {
      this.objectsById['' + o.getId()] = o;
      this.objects.push(o);
      try {
        this.linkObject(o, rawObject);
      } catch ( e ) {
        console.log(`Error addUpdateLocalItem linkObject ${e.getMessage()}`);
      }
      try {
        this.resolveObjectAttachmentRequets(o);
      } catch ( e ) {
        console.log(`Error addUpdateLocalItem resolveObjectAttachmentRequets ${e.getMessage()}`);
      }
      this.addNotifier.next(o);
      return o;
    } else {
      NotificationsService.trace(`3. ${this.getLoggingObjectTypeName()} id[${o.getId()}] already cached - updating in place`);
      try {
        this.updateLinks(existingObj, o, rawObject);
      } catch ( e ) {
        console.log(`Error addUpdateLocalItem updateLinks ${e.getMessage()}`);
      }
      try {
        this.resolveObjectAttachmentRequets(o);
      } catch ( e ) {
        console.log(`Error addUpdateLocalItem resolveObjectAttachmentRequets ${e.getMessage()}`);
      }
      try {
        existingObj.update(rawObject);
      } catch ( e ) {
        console.log(`Error addUpdateLocalItem ${e.getMessage()}`);
      }
      NotificationsService.trace(`5. ${this.getLoggingObjectTypeName()} id[${o.getId()}] firing updateNotifier`);
      this.updateNotifier.next(existingObj);
      return existingObj;
    }
  }

  linkObject(newObject: T, rawObject: T) {
    if ( newObject && rawObject ) {
      try {
        for (const data of this.getLinkData()) {
          switch (data.action) {
            case 'Link': {
              if (rawObject[data.objectProperty]) {
                this.notificationsService.addUpdateLocalItem(data.serviceName, rawObject[data.objectProperty]);
              }
              if (newObject[data.objectIdPropertyName]) {
                this.notificationsService.addObjectAttachRequest(data.serviceName, new ObjectAttachRequest(newObject[data.objectIdPropertyName], data.arrayProperty, data.objectProperty, newObject));
              }
              break;
            }
            case 'AddUpdateArray': {
              if (rawObject[data.arrayProperty]) {
                for (const object of rawObject[data.arrayProperty]) {
                  this.notificationsService.addUpdateLocalItem(data.serviceName, object);
                }
              }
              break;
            }
          }
        }
      } catch ( e ) {
        console.log(`Error in linkObject for [${this.getLoggingObjectTypeName()}] with linkData of [${this.getLinkData()}] newObject[${newObject}] rawObject[${rawObject}] error[${e.toString()}]`);
      }
    } else {
      console.log(`Error in linkObject for [${this.getLoggingObjectTypeName()}] with linkData of [${this.getLinkData()}] newObject[${newObject}] rawObject[${rawObject}] either newObject or rawObject is null`);
    }
  }

  updateLinks(existingObject: T, newObject: T, rawObject: T) {
    if ( existingObject && newObject && rawObject ) {
      try {
        for ( const data of this.getLinkData())  {
          switch ( data.action ) {
            case 'Link': {
              if ( rawObject[data.objectProperty] ) {
                this.notificationsService.addUpdateLocalItem(data.serviceName, rawObject[data.objectProperty]);
              }
              // The foreign key is only present on `newObject` when the server
              // actually included that column in the notification payload. A
              // payload that omits it must not be read as "moved to nothing" -
              // `undefined` means "no opinion", whereas 0 / null genuinely
              // means "unassigned" and DOES have to detach.
              const rawHasForeignKey = rawObject.hasOwnProperty(data.objectIdPropertyName)
                || newObject[data.objectIdPropertyName] !== undefined;
              if ( rawHasForeignKey ) {
                const newForeignKeyId = newObject[data.objectIdPropertyName];
                const oldForeignKeyId = existingObject[data.objectIdPropertyName];
                const staleLink = existingObject[data.objectProperty]
                  && typeof existingObject[data.objectProperty]['getId'] === 'function'
                  && existingObject[data.objectProperty].getId() !== newForeignKeyId;
                if ( oldForeignKeyId !== newForeignKeyId || staleLink ) {
                  NotificationsService.trace(`4. ${this.getLoggingObjectTypeName()} id[${existingObject.getId()}] `
                    + `re-parenting ${data.objectIdPropertyName} [${oldForeignKeyId}] -> [${newForeignKeyId}]`);
                  if ( existingObject[data.objectProperty] && existingObject[data.objectProperty][data.arrayProperty] ) {
                    const index = existingObject[data.objectProperty][data.arrayProperty].indexOf(existingObject, 0);
                    if (index > -1) {
                      existingObject[data.objectProperty][data.arrayProperty].splice(index, 1);
                      NotificationsService.trace(`4. detached from old ${data.objectProperty} ${data.arrayProperty}[${index}]`);
                    } else {
                      // The old parent's array did not contain this object by
                      // identity. That happens when a second instance of the
                      // same row is in the cache, and it leaves the item
                      // visible under BOTH parents.
                      NotificationsService.trace(`4. WARNING could not find ${this.getLoggingObjectTypeName()} `
                        + `id[${existingObject.getId()}] in old ${data.objectProperty}.${data.arrayProperty} - stale duplicate likely`);
                    }
                  }
                  delete existingObject[data.objectProperty];
                  existingObject[data.objectIdPropertyName] = newForeignKeyId;
                  if ( newForeignKeyId ) {
                    this.notificationsService.addObjectAttachRequest(data.serviceName, new ObjectAttachRequest(existingObject[data.objectIdPropertyName], data.arrayProperty, data.objectProperty, existingObject));
                  } else {
                    // Genuinely unassigned. Previously the whole branch was
                    // guarded by `if (newObject[...])`, so clearing a foreign
                    // key to 0 left the object attached to its old parent
                    // forever - the item stayed on screen under a set it no
                    // longer belonged to.
                    NotificationsService.trace(`4. ${data.objectIdPropertyName} cleared - left unattached`);
                  }
                }
              }
              break;
            }
            case 'AddUpdateArray': {
              if ( rawObject[data.arrayProperty] ) {
                for (const object of rawObject[data.arrayProperty]) {
                  this.notificationsService.addUpdateLocalItem(data.serviceName, object);
                }
              }
              break;
            }
          }
        }
      } catch ( e ) {
        console.log(`Error in linkObject for [${this.getLoggingObjectTypeName()}] with linkData of [${this.getLinkData()}] existingObject[${existingObject}] newObject[${newObject}] rawObject[${rawObject}] error[${e.toString()}]`);
      }
    } else {
      console.log(`Error in linkObject for [${this.getLoggingObjectTypeName()}] with linkData of [${this.getLinkData()}] existingObject[${existingObject}] newObject[${newObject}] rawObject[${rawObject}] one of  existingObject, newObject or rawObject is null`);
    }
  }

  deleteLocalItem (rawObject: T) {
    const o = this.makeObjectInstance(rawObject);
    const existingObj = this.getLocalItem(o.getId());
    if ( existingObj ) {
      let found = false;
      for ( let i = 0; i < this.objects.length && !found; i++) {
        if ( existingObj === this.objects[i] ) {
          delete this.objectsById['' + o.getId()];
          this.objects.splice(i, 1);
          this.deleteNotifier.next(existingObj);
          found = true;
        }
      }
    }
  }

  addObjectAttachRequest(objectAttachRequest: ObjectAttachRequest) {
    if ( !this.objectAttachRequests['' + objectAttachRequest.objectIdToAttachTo]) {
      this.objectAttachRequests['' + objectAttachRequest.objectIdToAttachTo] = [];
    }
    this.objectAttachRequests['' + objectAttachRequest.objectIdToAttachTo].push(objectAttachRequest);

    const object = this.getLocalItem(objectAttachRequest.objectIdToAttachTo);
    if ( object ) {
      try {
        this.resolveObjectAttachmentRequets(object);
      } catch ( e ) {
        console.log(`Error addingObjectAttachRequest ${e.getMessage()}`);
      }
    }
  }

  resolveObjectAttachmentRequets(object: T) {
    if ( this.objectAttachRequests[object.getId()] ) {
      const objectAttachmentRequests = this.objectAttachRequests[object.getId()];
      for ( const objectAtachmentRequest of objectAttachmentRequests ) {
        if ( !object[objectAtachmentRequest.arrayPropertyToAddTo] ) {
          object[objectAtachmentRequest.arrayPropertyToAddTo] = [];
          object[objectAtachmentRequest.arrayPropertyToAddTo].push(objectAtachmentRequest.objectToAttach);
          objectAtachmentRequest.objectToAttach[objectAtachmentRequest.objectPropertyToSet] = object;
        } else {
          const foundIndex = object[objectAtachmentRequest.arrayPropertyToAddTo].findIndex(item => item.getId() === objectAtachmentRequest.objectToAttach.getId());
          if ( foundIndex === -1) {
            object[objectAtachmentRequest.arrayPropertyToAddTo].push(objectAtachmentRequest.objectToAttach);
            objectAtachmentRequest.objectToAttach[objectAtachmentRequest.objectPropertyToSet] = object;
          }
        }
      }
      delete this.objectAttachRequests[object.getId()];
    }
  }

  addObjectDetachRequest(objectDetachRequest: ObjectDetachRequest) {
  }

  resolveAttachmentRequests(object: T) {
  }
}

